import React, { useState, useEffect } from "react";
import { useNavigate } from "react-router-dom";
import {
  auth,
  googleProvider,
  microsoftProvider,
  appleProvider,
} from "./firebase";
import {
  signInWithEmailAndPassword,
  createUserWithEmailAndPassword,
  signInWithPopup,
  signInWithRedirect,
  OAuthProvider,
  getRedirectResult,
  signInWithCustomToken,
  sendPasswordResetEmail, // <-- add
} from "firebase/auth";
import { useStore } from "../src/App/store";
import {
  FaGoogle,
  FaMicrosoft,
  FaApple,
  FaInfoCircle,
  FaTimes,
  FaEye,
  FaEyeSlash,
} from "react-icons/fa";
import { MdOutlineAlternateEmail, MdOpenInNew } from "react-icons/md";
import { useTranslation } from "react-i18next"; // Import useTranslation hook
import i18n from "./i18n"; // Import i18n configuration
import { APP_VERSION } from "./config";
// import { getUnlockedFeatures } from "./firestoreService";
import BurgerMenu from "./Components/BurgerMenu";
import devBanner from "./assets/images/preview.png"; // Import the dev-banner image
import {
  updateOrCreateUserDoc,
  getUserDataByEmail,
  getUserDoc,
} from "./storage/storageProxy"; // Import updateOrCreateUserDoc from firestoreService
import { User } from "./types/types";
import { ensureSignedIn, providerIsSignedIn } from "./auth/microsoftAuth";
import { getMicrosoftIdToken } from "./auth/microsoftAuth"; // <-- add
import { firebaseFunctionsUrl } from "./Helpers/FirebaseEnvironments";

const SignIn = () => {
  const { t } = useTranslation(); // Initialize translation hook
  const [email, setEmail] = useState("");
  const [password, setPassword] = useState("");
  const [error, setError] = useState("");
  const [showExplanation, setShowExplanation] = useState(false);
  const [showSignInForm, setShowSignInForm] = useState(false);
  const [showSignInModal, setShowSignInModal] = useState(false);
  const [showSignUp, setShowSignUp] = useState(true);
  const [showMenu, setShowMenu] = useState(false);
  const [showPasswordReset, setShowPasswordReset] = useState(false);
  const [resetEmail, setResetEmail] = useState("");
  const [resetMessage, setResetMessage] = useState("");
  const [showPassword, setShowPassword] = useState(false);
  // const [isFeatureUnlocked, setIsFeatureUnlocked] = useState<string[]>([]);

  const navigate = useNavigate();

  const handleSignIn = async (provider: any) => {
    try {
      auth.languageCode = "en";

      if (provider.providerId === "microsoft.com") {
        // Ensure M365 sign-in via MGT/MSAL
        await ensureSignedIn();

        // 1) Get Microsoft idToken
        const { idToken, email } = await getMicrosoftIdToken();

        // 2) Exchange for Firebase custom token
        const functionUrl = `${firebaseFunctionsUrl("microsoftCustomToken")}`;
        const resp = await fetch(functionUrl, {
          method: "POST",
          headers: { "Content-Type": "application/json" },
          body: JSON.stringify({ idToken: idToken, email: email }),
        });
        if (!resp.ok) {
          const errorText = await resp.text();
          throw new Error(
            `Token exchange failed (${resp.status}): ${errorText}`,
          );
        }
        const { firebaseToken } = await resp.json();

        // 3) Sign into Firebase using the custom token
        const result = await signInWithCustomToken(auth, firebaseToken);
        const user = result.user;

        // 4) Create/update user docs as before
        const useLocalData = await updateOrCreateUserDoc(user, false, email);
        useStore.getState().setUseLocalStorage(useLocalData);
        await updateOrCreateUserDoc(user, true, email);

        navigate("/");
        return;
      } else if (provider.providerId === "apple.com") {
        provider.setCustomParameters({
          // Apple supports these parameters:
          // https://firebase.google.com/docs/auth/web/apple
          prompt: "select_account",
          // response_mode: "query", // Not needed for Apple
          // scope: "email name", // Apple only supports "email name"
          scope: "email name",
          state: crypto.randomUUID(),
        });
        const result = await signInWithPopup(auth, provider);
        const user = result.user;
        const useLocalData = await updateOrCreateUserDoc(user, false);
        useStore.getState().setUseLocalStorage(useLocalData);
        await updateOrCreateUserDoc(user, true);
        navigate("/");
        return;
      } else {
        provider.setCustomParameters({
          prompt: "select_account",
          response_mode: "query",
          scope: "openid email profile",
          state: crypto.randomUUID(),
        });
        const result = await signInWithPopup(auth, provider);
        const user = result.user;
        const useLocalData = await updateOrCreateUserDoc(user, false);
        useStore.getState().setUseLocalStorage(useLocalData);
        await updateOrCreateUserDoc(user, true);
        navigate("/");
      }
    } catch (error) {
      if (error instanceof Error) {
        const errorCode = (error as any).code;
        if (errorCode === "auth/unauthorized-domain") {
          setError(
            "Sign-in failed: This domain is not authorized for authentication. Please check your Firebase Authentication settings (Authorized domains) or contact support.",
          );
        } else if (
          errorCode === "auth/internal-error" &&
          provider.providerId === "apple.com"
        ) {
          setError(
            "Apple sign-in failed: This is usually caused by missing or incorrect configuration in your Firebase project. Please ensure:\n" +
              "- Apple authentication is enabled in Firebase Authentication > Sign-in method\n" +
              "- All required Apple keys and services are set up in Firebase\n" +
              "- Your domain is listed under Authorized domains in Firebase Authentication settings\n" +
              "- Your Apple developer account is configured for Sign in with Apple\n\n" +
              "If you are the developer, see: https://firebase.google.com/docs/auth/web/apple\nIf you are a user, please contact support.",
          );
        } else if (
          errorCode === "auth/internal-error" &&
          provider.providerId === "microsoft.com"
        ) {
          setError(
            "Microsoft sign-in failed: This is usually caused by missing or incorrect configuration in your Firebase project. Please ensure:\n" +
              "- Microsoft authentication is enabled in Firebase Authentication > Sign-in method\n" +
              "- All required Microsoft Azure keys and services are set up in Firebase\n" +
              "- Your domain is listed under Authorized domains in Firebase Authentication settings\n\n" +
              "If you are the developer, see: https://firebase.google.com/docs/auth/web/microsoft-oauth\nIf you are a user, please contact support.",
          );
        } else {
          console.error("🔥 Error signing in:");
          console.error("➡️ Error Code:", errorCode);
          console.error("➡️ Error Message:", error.message);
          console.error("➡️ Full Error:", error);
          setError(t("error.signInFailed"));
        }
      } else {
        console.error("Error signing in:", error);
      }
      setError(t("error.signInFailed"));
    }
  };

  useEffect(() => {
    // Only run once on mount
    let unsub: (() => void) | undefined;
    getRedirectResult(auth)
      .then(async (result) => {
        // Debug: log the result and the current user
        console.log("getRedirectResult result:", result);
        console.log("auth:", auth);
        console.log("auth.currentUser after redirect:", auth.currentUser);

        if (result && result.user) {
          const user = result.user;
          const useLocalData = await updateOrCreateUserDoc(user, false);
          useStore.getState().setUseLocalStorage(useLocalData);
          await updateOrCreateUserDoc(user, true);
          navigate("/");
        } else {
          // Wait for auth state to be set (for redirect flows, this can be async)
          unsub = auth.onAuthStateChanged(async (user) => {
            if (user) {
              const useLocalData = await updateOrCreateUserDoc(user, false);
              useStore.getState().setUseLocalStorage(useLocalData);
              await updateOrCreateUserDoc(user, true);
              navigate("/");
            }
          });
        }
      })
      .catch((error) => {
        console.error("Error handling redirect result:", error);
      });
    // Cleanup on unmount
    return () => {
      if (unsub) unsub();
    };
  }, [navigate]);

  const handleEmailSignIn = async (event: React.FormEvent) => {
    event.preventDefault();
    try {
      const result = await signInWithEmailAndPassword(auth, email, password);
      const user = result.user;

      const userDoc = await getUserDoc(user.uid, false);
      if (userDoc) {
        const useLocalData = await updateOrCreateUserDoc(user, false); // update local user doc
        useStore.getState().setUseLocalStorage(useLocalData);
        await updateOrCreateUserDoc(user, false); // update cloud user doc
      }

      navigate("/");
    } catch (error: any) {
      console.error("Error signing in with email: ", error);
      setError(getErrorMessage(error.code));
    }
  };

  const handleEmailSignUp = async (event: React.FormEvent) => {
    event.preventDefault();
    try {
      const result = await createUserWithEmailAndPassword(
        auth,
        email,
        password,
      );
      const user = result.user;

      const userDoc = await getUserDoc(user.uid, false);
      const useLocalData = await updateOrCreateUserDoc(user, false); // update local user doc
      useStore.getState().setUseLocalStorage(useLocalData);
      await updateOrCreateUserDoc(user, true); // update cloud user doc

      navigate("/");
    } catch (error: any) {
      console.error("Error signing up with email: ", error);
      setError(getErrorMessage(error.code));
    }
  };

  const handlePasswordReset = async (event: React.FormEvent) => {
    event.preventDefault();
    try {
      await sendPasswordResetEmail(auth, resetEmail);
      setResetMessage("Password reset email sent! Check your inbox.");
      setError("");
      // Clear the form and message after 3 seconds
      setTimeout(() => {
        setResetMessage("");
        setShowPasswordReset(false);
        setResetEmail("");
      }, 3000);
    } catch (error: any) {
      console.error("Error sending password reset email: ", error);
      setResetMessage("");
      setError(getErrorMessage(error.code));
    }
  };

  const getErrorMessage = (errorCode: string) => {
    switch (errorCode) {
      case "auth/invalid-email":
        return t("error.invalidEmail");
      case "auth/user-disabled":
        return t("error.userDisabled");
      case "auth/user-not-found":
        return t("error.userNotFound");
      case "auth/wrong-password":
        return t("error.wrongPassword");
      default:
        return t("error.signInFailed");
    }
  };

  const toggleMenu = () => {
    setShowMenu(!showMenu);
  };

  const handlePrivacyPolicyClick = async () => {
    window.open("https://www.focusmap.online/privacy-policy", "_blank");
    setShowMenu(!showMenu);
  };

  const handleTermsAndConditionsClick = async () => {
    window.open("https://www.focusmap.online/terms-and-coniditions", "_blank");
    setShowMenu(!showMenu);
  };

  const handleSubscriptionPlansClick = async () => {
    window.open("https://www.focusmap.online/subscriptions", "_blank");
    setShowMenu(!showMenu);
  };

  const handleFocusMapSiteClick = async () => {
    window.open("https://www.focusmap.online", "_blank");
    setShowMenu(!showMenu);
  };

  return (
    <div className="sign-in-container">
      {/* {import.meta.env.VITE_ENV === "DEV" && (
        <img src={devBanner} className="banner" alt="FocusMap Logo" />
      )} */}
      <>
        <div className="language-selector">
          <button
            className="language-button"
            onClick={() => i18n.changeLanguage("en")}
          >
            EN
          </button>
          <button
            className="language-button"
            onClick={() => i18n.changeLanguage("da")}
          >
            DA
          </button>
        </div>

        <BurgerMenu
          showMenu={showMenu}
          toggleMenu={toggleMenu}
          //handleFocusMapSiteClick={handleFocusMapSiteClick}
          handleSubscriptionPlansClick={handleSubscriptionPlansClick}
          handleTermsAndConditionsClick={handleTermsAndConditionsClick}
          handlePrivacyPolicyClick={handlePrivacyPolicyClick}
          handleUserProfileClick={() => {}}
          handleSettingsClick={() => {}}
          handleSignOut={() => {}}
          isAuthenticated={false}
        />
      </>

      <div className="signin-title">
        {/* <div className="watermark">Free Plan by default</div> */}
        <div className="logo-text">FocusMap</div>
      </div>
      {/* <div className="tagline">{t("tagline")}</div> */}

      <div className="signin-description">{t("description")}</div>

      {showExplanation && (
        <div className="explanation">
          <div>
            <p>{t("explanation.part1")}</p>
          </div>
          <div>
            <p>{t("explanation.part2")}</p>
          </div>
          <div>
            <p>{t("explanation.part3")}</p>
            <button
              className="button official-homepage-button"
              onClick={() => window.open("https://focusmap.dk", "_blank")}
              style={{
                borderRadius: "50px",
                color: "white",
                backgroundColor: "#3C5973",
              }}
            >
              {t("officialHomePage")}
            </button>
            <p>
              {t("explanation.contactUs")}{" "}
              <a className="mailto" href="mailto:info@focusmap.dk">
                {t("explanation.contactEmail")}
              </a>
            </p>
          </div>
        </div>
      )}

      {!showSignInForm && (
        <>
          {/* <div className="signin-title"> */}
          {/* <div className="watermark">Start Free, Always</div> */}
          {/* </div> */}
          <button
            id="show-signin-options"
            className="button show-signin-options"
            onClick={() => {
              setShowSignInModal(true);
              setShowExplanation(false);
            }}
          >
            {t("signIn")}
          </button>
        </>
      )}

      {showSignInModal && (
        <div className="modal">
          <div className="modal-content">
            <h1 className="modal-header">Authentication</h1>{" "}
            {/* Added header */}
            <FaTimes
              className="close-icon"
              onClick={() => setShowSignInModal(false)}
            />
            <div>
              {error && <footer className="error">{error}</footer>}
              <form
                onSubmit={showSignUp ? handleEmailSignUp : handleEmailSignIn}
              >
                {showSignUp ? (
                  <>
                    <input
                      className="signin-input"
                      type="email"
                      placeholder={t("email")}
                      value={email}
                      onChange={(e) => setEmail(e.target.value)}
                      required
                    />
                    <div className="password-input-wrapper">
                      <input
                        className="signin-input password-input"
                        type={showPassword ? "text" : "password"}
                        placeholder={t("password")}
                        value={password}
                        onChange={(e) => setPassword(e.target.value)}
                        required
                        autoComplete="current-password"
                      />
                      <button
                        type="button"
                        className="password-toggle-btn"
                        onMouseDown={() => setShowPassword(true)}
                        onMouseUp={() => setShowPassword(false)}
                        onMouseLeave={() => setShowPassword(false)}
                        onTouchStart={() => setShowPassword(true)}
                        onTouchEnd={() => setShowPassword(false)}
                      >
                        {showPassword ? <FaEyeSlash /> : <FaEye />}
                      </button>
                    </div>
                    <div className="auth-buttons">
                      <button className="button" type="submit">
                        <div
                          style={{
                            display: "flex",
                            flexDirection: "row",
                            justifyContent: "center",
                            alignItems: "center",
                          }}
                        >
                          <MdOutlineAlternateEmail
                            size={24}
                            color="#3C5973"
                            className="auth-icon"
                          />
                          <div className="auth-text">
                            {t("signUpWithEmail")}
                          </div>
                        </div>
                      </button>
                    </div>
                    <p className="signin-link-text">
                      {t("Already have an account?")}{" "}
                      <a
                        href="#"
                        onClick={() => setShowSignUp(false)}
                        className="signin-link"
                      >
                        {t("Sign In")}
                      </a>
                    </p>
                  </>
                ) : (
                  <>
                    <input
                      className="signin-input"
                      type="email"
                      placeholder={t("email")}
                      value={email}
                      onChange={(e) => setEmail(e.target.value)}
                      required
                      autoComplete="username"
                    />
                    <div className="password-input-wrapper">
                      <input
                        className="signin-input password-input"
                        type={showPassword ? "text" : "password"}
                        placeholder={t("password")}
                        value={password}
                        onChange={(e) => setPassword(e.target.value)}
                        required
                        autoComplete="current-password"
                      />
                      <button
                        type="button"
                        className="password-toggle-btn"
                        onMouseDown={() => setShowPassword(true)}
                        onMouseUp={() => setShowPassword(false)}
                        onMouseLeave={() => setShowPassword(false)}
                        onTouchStart={() => setShowPassword(true)}
                        onTouchEnd={() => setShowPassword(false)}
                      >
                        {showPassword ? <FaEyeSlash /> : <FaEye />}
                      </button>
                    </div>
                    <div className="auth-buttons">
                      <button className="button" type="submit">
                        <div
                          style={{
                            display: "flex",
                            flexDirection: "row",
                            justifyContent: "center",
                            alignItems: "center",
                          }}
                        >
                          <MdOutlineAlternateEmail
                            size={24}
                            color="#3C5973"
                            className="auth-icon"
                          />
                          <div className="auth-text">
                            {t("signInWithEmail")}
                          </div>
                        </div>
                      </button>
                    </div>
                    <p className="forgot-password-text">
                      <a
                        href="#"
                        onClick={(e) => {
                          e.preventDefault();
                          setShowPasswordReset(true);
                          setError("");
                          setResetMessage("");
                        }}
                        className="forgot-password-link"
                      >
                        Forgot your password?
                      </a>
                    </p>
                    <p className="signup-link-text">
                      {t("Don't have an account yet?")}{" "}
                      <a
                        href="#"
                        onClick={() => setShowSignUp(true)}
                        className="signup-link"
                      >
                        {t("Sign up")}
                      </a>
                    </p>
                  </>
                )}
              </form>
              {/* <hr className="divider" />
              <div className="login-options">
                <button
                  className="button"
                  onClick={() => handleSignIn(googleProvider)}
                >
                  <div className="button-content">
                    <FaGoogle size={24} color="white" className="auth-icon" />
                    <div className="auth-text">{t("signInWithGoogle")}</div>
                  </div>
                </button>

                <button
                  //disabled
                  className="button"
                  onClick={() => handleSignIn(microsoftProvider)}
                >
                  <div className="button-content">
                    <FaMicrosoft
                      size={24}
                      color="white"
                      className="auth-icon"
                    />
                    <div className="auth-text">{t("signInWithMicrosoft")}</div>
                  </div>
                </button>

                <button
                  disabled
                  className="button"
                  onClick={() => handleSignIn(appleProvider)}
                >
                  <div className="button-content">
                    <FaApple size={24} color="white" className="auth-icon" />
                    <div className="auth-text">{t("signInWithApple")}</div>
                  </div>
                </button>
              </div> */}
            </div>
          </div>
        </div>
      )}

      {showPasswordReset && (
        <div className="modal">
          <div className="modal-content">
            <h1 className="modal-header">Reset Password</h1>
            <FaTimes
              className="close-icon"
              onClick={() => {
                setShowPasswordReset(false);
                setResetEmail("");
                setResetMessage("");
                setError("");
              }}
            />
            <div>
              {error && <footer className="error">{error}</footer>}
              {resetMessage && (
                <p className="success-message">{resetMessage}</p>
              )}
              <form onSubmit={handlePasswordReset}>
                <input
                  className="signin-input"
                  type="email"
                  placeholder="Enter your email"
                  value={resetEmail}
                  onChange={(e) => setResetEmail(e.target.value)}
                  required
                  autoComplete="email"
                />
                <div className="auth-buttons">
                  <button className="button" type="submit">
                    <div
                      style={{
                        display: "flex",
                        flexDirection: "row",
                        justifyContent: "center",
                        alignItems: "center",
                      }}
                    >
                      <MdOutlineAlternateEmail
                        size={24}
                        color="#3C5973"
                        className="auth-icon"
                      />
                      <div className="auth-text">Send Reset Email</div>
                    </div>
                  </button>
                </div>
                <p className="signup-link-text">
                  Remember your password?{" "}
                  <a
                    href="#"
                    onClick={(e) => {
                      e.preventDefault();
                      setShowPasswordReset(false);
                      setResetEmail("");
                      setResetMessage("");
                      setError("");
                    }}
                    className="signup-link"
                  >
                    Back to Sign In
                  </a>
                </p>
              </form>
            </div>
          </div>
        </div>
      )}

      <div className="agreement">
        <p>
          By proceeding, you agree to the
          <br />
          <a
            className="agreement-link"
            href="https://www.focusmap.dk/terms-and-conditions"
            target="_blank"
          >
            {" "}
            Terms of Service
          </a>
          &nbsp;and
          <a
            className="agreement-link"
            href="https://www.focusmap.dk/privacy-policy"
            target="_blank"
          >
            {" "}
            Privacy Policy.
          </a>
        </p>
      </div>

      <div className="why">
        {t("wantToKnowMore")}
        <MdOpenInNew
          className="info-icon"
          onClick={() => window.open("https://focusmap.dk", "_blank")}
        />
      </div>

      <footer>
        <p className="version">Version: {APP_VERSION}</p>
      </footer>
    </div>
  );
};

export default SignIn;
